import { ArrowRight } from "lucide-react";
import CTABanner from "@/components/CTABanner";
import Reveal from "@/components/Reveal";
import { useContactModal } from "@/context/ContactModalContext";
import { IMAGES, SERVICES } from "@/lib/content";

const Services = () => {
  const { open } = useContactModal();

  return (
    <div data-testid="services-page">
      {/* INTRO */}
      <section className="border-b border-hairline">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-16 lg:pt-16 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <span className="eyebrow">Services —</span>
              <h1 className="mt-8 font-serif text-[44px] sm:text-[56px] lg:text-[68px] leading-[1.05] text-offwhite tracking-[-0.015em]">
                How we help
              </h1>
            </div>
            <div className="lg:col-span-5 lg:pt-8">
              <p className="text-[15px] md:text-[16px] leading-[1.75] text-offwhite/65 max-w-[44ch]">
                We provide strategic and regulatory advice to help companies
                navigate complexity, manage risk, and unlock growth — across
                four connected practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE GRID — image cards */}
      <section data-testid="services-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {SERVICES.map((s, idx) => (
              <Reveal key={s.key} delay={idx * 100}>
                <article
                  data-testid={`service-card-${s.key}`}
                  className="group bg-[#0E0E0E] border border-hairline hover:border-gold/40 transition-colors duration-300"
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img
                      src={IMAGES[s.image]}
                      alt={s.title}
                      className={`w-full h-full object-cover brightness-[1.12] contrast-[1.04] saturate-[1.03] transition-transform duration-700 group-hover:scale-[1.04] ${s.key === "strategy" ? "object-[center_62%]" : ""} ${s.key === "regulatory" ? "object-[center_64%]" : ""} ${s.key === "digital" ? "object-[58%_58%]" : ""} ${s.key === "transactions" ? "object-[center_66%]" : ""}`}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-[#0A0A0A]/10 to-transparent" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-[24px] text-[#D4B66F] mb-3">
                      {s.title}
                    </h3>
                    <p className="text-[13.5px] leading-[1.75] text-[#8A8A8A]">
                      {s.short}
                    </p>
                    <div className="mt-6 inline-flex items-center text-gold/80 group-hover:text-gold transition-colors">
                      <ArrowRight className="hover-arrow" size={16} />
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILED SECTIONS */}
      <section
        data-testid="services-detail"
        className="border-t border-hairline bg-[#0B0B0B]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 space-y-20 lg:space-y-28">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.key}>
              <div
                data-testid={`service-detail-${s.key}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
              >
                <div className="lg:col-span-3">
                  <span className="eyebrow">
                    0{idx + 1} — {s.title}
                  </span>
                  <h2 className="mt-6 font-serif text-[34px] lg:text-[40px] leading-[1.1] text-offwhite">
                    {s.title}
                  </h2>
                </div>
                <div className="lg:col-span-5">
                  <p className="text-[15px] leading-[1.8] text-offwhite/70">
                    {s.long}
                  </p>
                </div>
                <div className="lg:col-span-4">
                  <ul className="divide-y divide-[rgba(245,242,232,0.08)] border-y border-hairline">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="py-4 flex items-start gap-3 text-[14px] text-offwhite/85"
                      >
                        <span className="text-gold mt-0.5">—</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner />
    </div>
  );
};

export default Services;
